import { useCallback } from 'react';
import { noop } from 'lodash';
import { useApolloClient } from '@frameio/graphql-client';
import type { ShareSettingsFragment } from '@frameio/graphql-client/generated';
import { ShareSettings } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useDataContextMenu } from '@frameio/shared/hooks/useDataContextMenu';
import type { CopyLinkSourceForAnalytics } from '@frameio/shared/shares/hooks/useCopyShareLink';
import { useUpdateShareSettings } from '@frameio/shared/shares/share-builder/hooks';
import { getIsShareVisible } from '@frameio/shared/shares/utils';
import { EMPTY_OBJECT } from '@frameio/utils';
import { useToast } from '@frameio/vapor';
import type { ShareActionsMenuProps } from '../components/ShareActionsMenu';
import {
  ShareActionsMenu,
  ShareActionsMenuItems,
} from '../components/ShareActionsMenu';
import { renderShareAccessToast } from './renderShareAccessToast';
import { useBulkToggleShareVisibility } from './useBulkToggleShareVisibility';
import { useBulkDeleteShares, useDeleteShare } from './useDeleteShare';
import { useDuplicateShare } from './useDuplicateShare';

export const UseShareActionsMenuItems = ShareActionsMenuItems;

export type UseShareActionsMenuArgs = {
  onCompleteMutation?: ({
    resultShareId,
    type,
  }: {
    resultShareId: string;
    type: keyof typeof UseShareActionsMenuItems;
  }) => void;
  shareIds?: Array<string>;
} & Pick<ShareActionsMenuProps, 'menuItems' | 'onSelectRenameShare'> &
  CopyLinkSourceForAnalytics;

export const useShareActionsMenu = ({
  actionSource,
  menuItems,
  onCompleteMutation = noop,
  onSelectRenameShare,
  shareIds: shareIdsProp,
}: UseShareActionsMenuArgs = EMPTY_OBJECT) => {
  const { cache } = useApolloClient();
  const projectId = useActiveProjectId();

  const {
    contextMenuAnchorPosition,
    handleContextMenuEvent,
    isContextMenuOpen,
    setIsContextMenuOpen,
  } = useDataContextMenu();

  const { deleteDialog, requestDelete } = useDeleteShare();
  const { deleteDialog: bulkDeleteDialog, requestDelete: requestBulkDelete } =
    useBulkDeleteShares({
      projectId,
      shareIds: shareIdsProp ?? [],
    });

  const { actionType, bulkToggleVisibility } = useBulkToggleShareVisibility({
    projectId,
    shareIds: shareIdsProp ?? [],
  });

  const { addToast } = useToast();

  const updateSettings = useUpdateShareSettings({
    onCompleted: (data) => {
      if (data?.updateShare.share) {
        renderShareAccessToast(addToast, {
          variant: 'success',
          shareName: data?.updateShare?.share?.name,
        });

        onCompleteMutation({
          resultShareId: data.updateShare.share.id,
          type: UseShareActionsMenuItems.RENAME_SHARE,
        });
      }
    },
    onError: () => {
      renderShareAccessToast(addToast, { variant: 'error' });
    },
  });

  const duplicateShare = useDuplicateShare({
    onCompleted: (data) => {
      if (data.duplicateShare?.share.id) {
        onCompleteMutation({
          resultShareId: data.duplicateShare.share.id,
          type: UseShareActionsMenuItems.DUPLICATE,
        });
      }
    },
  });

  const getShareActionsMenuContent = useCallback(
    (shareId?: string) => {
      /**
       * @fixme This is pretty messy. Ideally there'd be one source of truth here for defining `shareIds`
       * in the `ShareActionsMenu`. Hopefully, some day, there will be. But it won't be today.
       */
      let shareIds: string[] | undefined;

      if (shareIdsProp && shareIdsProp.length > 0) {
        shareIds = shareIdsProp;
      } else if (shareId) {
        shareIds = [shareId];
      }

      /** If `shareIds` remains unassigned there's nothing for the `ShareActionsMenu` to do. */
      if (!shareIds) {
        return null;
      }

      const shareFragment = cache.readFragment<ShareSettingsFragment>({
        fragment: ShareSettings,
        fragmentName: 'ShareSettings',
        id: cache.identify({ __typename: 'Share', id: shareId }),
        returnPartialData: true,
      });

      const enabled = getIsShareVisible({
        enabled: Boolean(shareFragment?.enabled),
        expiration: shareFragment?.expiration,
        expiresIn: shareFragment?.expiresIn,
      });

      return (
        <ShareActionsMenu
          key={shareIds[0]}
          actionSource={actionSource}
          canBeRenamed={!shareFragment?.useOriginHeader}
          isShareDisabled={shareId ? !enabled : actionType === 'enable'}
          menuItems={menuItems}
          onDelete={() => {
            if (shareFragment) {
              requestDelete({
                id: shareFragment?.id,
                name: shareFragment?.name,
              });
            } else if (shareIds) {
              requestBulkDelete();
            }
          }}
          onDisableAccess={() => {
            if (shareFragment) {
              updateSettings({
                share: shareFragment,
                variables: { enabled: false, shareId: shareIds[0] },
              });
            } else if (actionType === 'disable') {
              bulkToggleVisibility();
            }
          }}
          onDuplicateShare={() => duplicateShare({ shareId: shareIds[0] })}
          onEnableAccess={() => {
            if (shareFragment) {
              updateSettings({
                share: shareFragment,
                variables: { enabled: true, shareId: shareIds[0] },
              });
            } else if (actionType === 'enable') {
              bulkToggleVisibility();
            }
          }}
          onSelectRenameShare={() => onSelectRenameShare?.(shareId)}
          projectId={projectId}
          shareIds={shareIds}
          shareUrl={shareFragment?.url ?? ''}
        />
      );
    },
    [
      actionSource,
      actionType,
      bulkToggleVisibility,
      cache,
      duplicateShare,
      menuItems,
      onSelectRenameShare,
      projectId,
      requestBulkDelete,
      requestDelete,
      shareIdsProp,
      updateSettings,
    ]
  );

  return {
    contextMenuAnchorPosition,
    getShareActionsMenuContent,
    handleContextMenuEvent,
    isContextMenuOpen,
    setIsContextMenuOpen,
    shareActionsDeleteDialog: shareIdsProp ? bulkDeleteDialog : deleteDialog,
  };
};
